import { z } from 'zod';
/**
 * Контракт K4 — служебное чтение алертов риска по списаниям Stripe.
 *
 * `POST /internal/risk/alerts/by-charges` (admin-api) — НЕ админский маршрут:
 * его вызывает wit-payment-service (таймлайны исходов покупок для AI-агента)
 * с `Authorization: Bearer <RISK_READ_API_TOKEN>`, без сессии и RBAC. Живёт в
 * этом пакете, потому что это HTTP-контракт сервера, а сервер — admin-api.
 *
 * Две привязки, обе — через КЕЙС, и тогда отдаются ВСЕ алерты кейса
 * (включая дубликаты — с `duplicateClass`):
 *  - `linkedBy: 'charge'` — `risk_case.identification_status = 'resolved'` И
 *    `risk_case.matched_charge_id ∈ chargeIds`. Только у `resolved` колонка
 *    достоверна: автоматическое переопознание (`ambiguous`, single-customer)
 *    оставляет в ней прежнее списание, и реактор ей тоже верит только при
 *    `resolved`.
 *  - `linkedBy: 'user'` — кейс НЕ привязан к запрошенному списанию, но
 *    `lower(risk_case.wit_user_id) ∈ userIds`. Это кейсы, опознанные вручную,
 *    по клиенту без однозначного списания или с недостоверным (устаревшим)
 *    списанием. У таких строк `chargeId = matchedChargeId = null` ВСЕГДА —
 *    утверждения «алерт про это списание» здесь нет.
 * Кейс в ответе ровно один раз: если он попал по обоим ключам — `charge`.
 * Подробно — `docs/agent-data-mcp.md` → «Chargeblast для платёжки».
 *
 * В ответе НЕТ e-mail, id клиента у провайдера, BIN, last4, ARN, auth-кода,
 * сырого payload: схема ответа `strict()`, лишний ключ — ошибка разбора.
 */
/** Stripe charge id. Тот же вид, что принимает рефанд-контракт платёжки. */
export const StripeChargeId = z.string().regex(/^ch_[A-Za-z0-9]+$/, 'expected a Stripe charge id');
/**
 * Наш id пользователя WIT (`risk_case.wit_user_id`) — Mongo ObjectId,
 * 24 hex-символа. Регистр на входе не важен: значение приводится к нижнему
 * (каноническая форма Mongo), а сравнение с базой — `lower(wit_user_id)`.
 */
export const WitUserId = z
    .string()
    .regex(/^[a-fA-F0-9]{24}$/, 'expected a WIT user id (24 hex)')
    .transform((s) => s.toLowerCase());
/** Верхняя граница КАЖДОГО из списков — договорённость контракта K4. */
export const RISK_ALERTS_BY_CHARGES_MAX = 1000;
/**
 * Оба списка необязательны, но хотя бы один — непустой. Старый вид
 * `{ chargeIds }` по-прежнему валиден.
 */
export const RiskAlertsByChargesRequest = z
    .object({
    chargeIds: z.array(StripeChargeId).max(RISK_ALERTS_BY_CHARGES_MAX).optional(),
    userIds: z.array(WitUserId).max(RISK_ALERTS_BY_CHARGES_MAX).optional(),
})
    .strict()
    .refine((b) => (b.chargeIds?.length ?? 0) + (b.userIds?.length ?? 0) > 0, {
    message: 'at least one of chargeIds / userIds must be non-empty',
});
/** Как кейс попал в ответ: по списанию или (только если не по нему) по пользователю. */
export const RiskAlertLinkedBy = z.enum(['charge', 'user']);
export const RiskChargeAlertItem = z
    .object({
    /** = `matchedChargeId` (историческое имя); `null` у всех строк `linkedBy: 'user'`. */
    chargeId: z.string().nullable(),
    linkedBy: RiskAlertLinkedBy,
    /**
     * `risk_case.matched_charge_id` — только у `linkedBy: 'charge'` (кейс
     * `resolved`); у `linkedBy: 'user'` всегда `null`, даже если колонка в
     * базе заполнена.
     */
    matchedChargeId: z.string().nullable(),
    /** `risk_case.wit_user_id` — опознанный пользователь WIT. */
    witUserId: z.string().nullable(),
    /** `pending` | `resolved` | `ambiguous` | `not_found` | `unavailable`; терпеть новые. */
    identificationStatus: z.string(),
    alertId: z.string(),
    /** Сегодня всегда `chargeblast`; потребитель обязан терпеть новые значения. */
    source: z.string(),
    sourceAlertId: z.string(),
    /**
     * Вид алерта в модели support-bot: `fraud` | `dispute` | `rdr` | `cancel` |
     * `info` | `unknown`; потребитель обязан терпеть новые значения.
     */
    kind: z.string(),
    /** Сеть/канал источника (Visa, Mastercard, …) как прислал провайдер. */
    network: z.string().nullable(),
    alertCreatedAt: z.string().nullable(),
    transactionAt: z.string().nullable(),
    /**
     * `risk_alert.first_seen_at` — когда support-bot впервые увидел алерт
     * (ISO UTC). Чем датировать алерт, у которого `alertCreatedAt = null`.
     */
    firstSeenAt: z.string().nullable(),
    amountCents: z.number().int().nullable(),
    currency: z.string().nullable(),
    reasonCode: z.string().nullable(),
    /** `none` | `intra_source` | `cross_source`. */
    duplicateClass: z.string(),
    caseId: z.string(),
    caseNumber: z.number().int(),
    caseStatus: z.string(),
    reactionStatus: z.string().nullable(),
    /** Флаг провайдера на алерте («уже возвращено»), не наш журнал возвратов. */
    isRefunded: z.boolean(),
})
    .strict();
export const RiskAlertsByChargesResponse = z
    .object({
    items: z.array(RiskChargeAlertItem),
})
    .strict();
/**
 * Маршрут K4 — единственный источник метода и пути: admin-api монтирует
 * обработчик по нему (`routes/internal-risk.ts`), документация MCP берёт
 * отсюда же путь и схемы.
 */
export const RISK_ALERTS_BY_CHARGES_ROUTE = {
    method: 'POST',
    path: '/internal/risk/alerts/by-charges',
    request: RiskAlertsByChargesRequest,
    response: RiskAlertsByChargesResponse,
};
//# sourceMappingURL=internal-risk.js.map